import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  Typography,
  Paper,
  Button,
  Box,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import {
  getSoftwareUpdateByChannel,
  SoftwareUpdateResponse,
  SoftwareChannel,
  getSoftwareUpdateInformation,
} from '../../services/api';
import { rewriteToCdn } from '../../utils/cdn';
import { RootState } from '../../store';
import { useI18n } from '../../i18n/useI18n';
import UserPageShell, { userCardEnterSx } from '../shared/UserPageShell';

const Software: React.FC = () => {
  const { pages: t, formatMessage } = useI18n();
  const { user } = useSelector((state: RootState) => state.auth);
  const [updateInfo, setUpdateInfo] = useState<SoftwareUpdateResponse | null>(null);
  const deviceAccess = {
    pro: user?.deviceAccess?.pro ?? true,
    lite: user?.deviceAccess?.lite ?? false,
    mono: user?.deviceAccess?.mono ?? false,
  };
  const availableChannels = (['pro', 'lite', 'mono'] as SoftwareChannel[]).filter((channel) => deviceAccess[channel]);
  const defaultChannel = availableChannels[0] || 'pro';
  const [activeChannel, setActiveChannel] = useState<SoftwareChannel>(defaultChannel);
  const [loading, setLoading] = useState(true);
  const [updateInformation, setUpdateInformation] = useState<string>('');
  const [loadingInfo, setLoadingInfo] = useState(true);

  const fetchUpdateInfo = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getSoftwareUpdateByChannel(activeChannel);
      setUpdateInfo(data);
    } catch (error: any) {
      console.error('Error fetching software update:', error);
      toast.error('Không thể tải thông tin cập nhật');
    } finally {
      setLoading(false);
    }
  }, [activeChannel]);

  useEffect(() => {
    if (!availableChannels.includes(activeChannel)) {
      setActiveChannel(defaultChannel);
    }
  }, [activeChannel, availableChannels, defaultChannel]);

  useEffect(() => {
    fetchUpdateInfo();
  }, [fetchUpdateInfo]);

  useEffect(() => {
    fetchUpdateInformation();
  }, []);

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('vi-VN');
  };

  const fetchUpdateInformation = async () => {
    try {
      setLoadingInfo(true);
      const data = await getSoftwareUpdateInformation();
      setUpdateInformation(data.updateInformation || '');
    } catch (error: any) {
      console.error('Error fetching update information:', error);
      setUpdateInformation('');
    } finally {
      setLoadingInfo(false);
    }
  };

  return (
    <UserPageShell maxWidth="md" enter>
      <Paper elevation={0} sx={{ p: { xs: 2, sm: 4 }, borderRadius: 3, border: '1px solid', borderColor: 'divider', ...userCardEnterSx(0) }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            {t.software.title}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchUpdateInfo}
            disabled={loading}
          >
            {t.common.refresh}
          </Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
          {availableChannels.map((channel) => (
            <Button
              key={channel}
              variant={activeChannel === channel ? 'contained' : 'outlined'}
              onClick={() => setActiveChannel(channel)}
              sx={{ textTransform: 'uppercase' }}
            >
              {channel}
            </Button>
          ))}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {updateInfo?.hasFile ? (
              <Card sx={userCardEnterSx(1)}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box
                      component="img"
                      src="/m-photo-logo.png"
                      alt="M-Photo Logo"
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        objectFit: 'cover',
                        mr: 2,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      }}
                    />
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {formatMessage(t.software.latestVersion, {
                          channel: activeChannel.toUpperCase(),
                        })}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    <strong>{t.software.fileName}</strong> {updateInfo.fileName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    <strong>{t.software.fileSize}</strong> {formatFileSize(updateInfo.fileSize)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    <strong>{t.software.uploadDate}</strong> {formatDate(updateInfo.dateUpload)}
                  </Typography>
                </CardContent>
                <CardActions>
                  {updateInfo.fileUrl && updateInfo.fileName && (
                    <Button
                      variant="contained"
                      component="a"
                      startIcon={<DownloadIcon />}
                      href={rewriteToCdn(updateInfo.fileUrl)}
                      download={updateInfo.fileName}
                      sx={{ ml: 1 }}
                    >
                      {t.software.download}
                    </Button>
                  )}
                </CardActions>
              </Card>
            ) : (
              <Alert severity="info">
                {t.software.noUpdate}
              </Alert>
            )}

            {/* Update Information Display */}
            {!loadingInfo && updateInformation && (
              <Box sx={{ mt: 4 }}>
                <Typography variant="h5" component="h2" gutterBottom>
                  {t.software.updateInfo}
                </Typography>
                <Paper 
                  sx={{ 
                    p: 3, 
                    mt: 2,
                    backgroundColor: 'background.paper',
                    '& .ql-editor': {
                      minHeight: '200px',
                      fontSize: '1rem',
                    },
                    '& .ql-editor img': {
                      maxWidth: '100%',
                      height: 'auto',
                    }
                  }}
                >
                  <Box
                    dangerouslySetInnerHTML={{ __html: updateInformation }}
                    sx={{
                      '& img': {
                        maxWidth: '100%',
                        height: 'auto',
                        borderRadius: 1,
                        my: 2,
                      },
                      '& p': {
                        mb: 2,
                      },
                      '& ul, & ol': {
                        pl: 3,
                        mb: 2,
                      },
                      '& h1, & h2, & h3': {
                        mt: 2,
                        mb: 1,
                      },
                    }}
                  />
                </Paper>
              </Box>
            )}
          </>
        )}
      </Paper>
    </UserPageShell>
  );
};

export default Software;

